import React, { useEffect, useState } from "react";
import { referralApi } from "../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Share2, Loader2, Save } from "lucide-react";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-foreground/40 mt-1">{hint}</p>}
  </div>
);

export default function ReferralSettings({ session }) {
  const [s, setS] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    referralApi.getSettings(session).then(setS).catch(() => toast.error("Failed to load settings"));
    referralApi.adminStats(session).then(setStats).catch(() => {});
  }, [session]);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await referralApi.saveSettings(s, session); toast.success("Referral settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="flex items-center gap-2 text-sm text-foreground/50 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const S = stats?.by_status || {};

  return (
    <div className="space-y-6" data-testid="referral-settings">
      <div className="flex items-center gap-2">
        <Share2 className="h-4 w-4 text-primary" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">Referral Program</h2>
          <p className="text-xs text-foreground/40">Let customers invite other organizations. Each invite becomes an attributed lead.</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="referral-stats">
          {[["Total", stats.total], ["Registered", S["Registered"] || 0], ["Pilot", S["Pilot Active"] || 0], ["Converted", stats.converted], ["Conv. %", `${stats.conversion_pct}%`]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-border bg-muted/20 p-3">
              <div className="text-2xl font-bold text-foreground">{v}</div>
              <div className="text-[11px] uppercase tracking-wider text-foreground/40 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-border p-4 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
          <input type="checkbox" data-testid="ref-enabled" checked={s.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enable sharing
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Daily limit (per user)" hint="Prevents abuse.">
            <Input data-testid="ref-limit" type="number" value={s.daily_limit} onChange={(e) => set("daily_limit", Number(e.target.value))} />
          </Field>
          <Field label="Referral expiration (days)">
            <Input data-testid="ref-expiry" type="number" value={s.expiration_days} onChange={(e) => set("expiration_days", Number(e.target.value))} />
          </Field>
        </div>
        <Field label="Landing page URL" hint="Where the invite link points (relative or absolute).">
          <Input data-testid="ref-landing" value={s.landing_url} onChange={(e) => set("landing_url", e.target.value)} />
        </Field>
        <Field label="Email subject">
          <Input data-testid="ref-subject" value={s.email_subject} onChange={(e) => set("email_subject", e.target.value)} />
        </Field>
        <Field label="Email intro (optional)" hint="Added to the branded invite email.">
          <Textarea data-testid="ref-intro" rows={2} value={s.email_intro} onChange={(e) => set("email_intro", e.target.value)} />
        </Field>
        <Button onClick={save} disabled={saving} data-testid="ref-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save settings
        </Button>
      </div>
    </div>
  );
}
